<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from 'primevue/usetoast'
import { useUsersStore } from '@/store/modules/user'
import { useAuthStore } from '@/store/modules/auth'
import { Trash2, User, ShieldCheck, LogOut, Mail, UserCircle } from 'lucide-vue-next'
import { IconGoogle, IconApple } from '@/components/icons'
import { IconQRCode } from '@/components/icons/lucide-vue'
import AppModal from '@/components/shared/AppModal.vue'
import LinkAccountsModal from './LinkAccountsModal.vue'
import SessionsTable from './SessionsTable.vue'
import QrModal from '@/components/share/QrModal.vue'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useRoute } from 'vue-router'
import { getBaseUrl } from '@/utils/shareUrlUtils'
import { createShareService } from '@/services/share.service'
import { useStore } from 'vuex'
import type { Session, LogoData } from '@/types/client'

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const usersStore = useUsersStore()
const authStore = useAuthStore()
const toast = useToast()
const route = useRoute()
const isOdds = computed(() => route?.path.startsWith('/odds'))

const activeTab = ref<'profile' | 'sessions'>('profile')
const originalUsername = ref('')
const editedUsername = ref('')
const loading = ref(false)
const sessions = ref<Session[]>([])

const deleteModalVisible = ref(false)
const linkModalVisible = ref(false)
const confirmLogoutAllVisible = ref(false)
const showQrModal = ref(false)
const unlinkModalVisible = ref(false)
const providerToUnlink = ref<any>(null)
const qrUrl = ref('')
const logoData = ref<LogoData | null>(null)

const store = useStore()
const recaptcha = useReCaptcha()
const executeRecaptcha = recaptcha?.executeRecaptcha
const shareService = createShareService(store)

watch(
  () => props.visible,
  (visible) => {
    if (visible && usersStore.user) {
      originalUsername.value = usersStore.user.username
      editedUsername.value = usersStore.user.username
      activeTab.value = 'profile'
      loadSessions()
    }
  },
  { immediate: true }
)

const isUsernameChanged = computed(() => editedUsername.value !== originalUsername.value)

const currentSession = computed<Session[]>(() => {
  const session = sessions.value.find(s => s.current)
  return session ? [session] : []
})

const otherSessions = computed<Session[]>(() =>
  sessions.value.filter(s => !s.current)
)

async function loadSessions() {
  if (!props.visible) return
  loading.value = true
  try {
    const res: Session[] = await authStore.getSessions()
    sessions.value = res
  } catch (e: any) {
    toast.add({ severity: 'error', summary: 'Failed to load sessions', life: 5000 })
  } finally {
    loading.value = false
  }
}

async function updateUsername() {
  if (!isUsernameChanged.value) return
  try {
    loading.value = true
    await usersStore.saveUser(editedUsername.value)
    originalUsername.value = editedUsername.value
    toast.add({ severity: 'success', summary: 'Username updated successfully', life: 5000 })
  } catch (e: any) {
    toast.add({ severity: 'error', summary: 'Failed to update username', life: 5000 })
  } finally {
    loading.value = false
  }
}

async function logoutSession(sessionId: number) {
  try {
    await authStore.logoutSession(sessionId)
    sessions.value = sessions.value.filter((s) => s.id !== sessionId)
    toast.add({ severity: 'success', summary: 'Session logged out', life: 5000 })
  } catch (e: any) {
    toast.add({ severity: 'error', summary: 'Failed to log out session', life: 5000 })
  }
}

async function logoutAllDevices() {
  try {
    loading.value = true
    await authStore.logoutAllDevices()
    sessions.value = []
    toast.add({ severity: 'success', summary: 'Logged out of all devices', life: 5000 })
    emit('update:visible', false)
  } catch (e: any) {
    toast.add({ severity: 'error', summary: 'Failed to log out all sessions', life: 5000 })
  } finally {
    loading.value = false
    confirmLogoutAllVisible.value = false
  }
}

function requestUnlink(provider: any) {
  providerToUnlink.value = provider
  unlinkModalVisible.value = true
}

async function confirmUnlink() {
  if (!providerToUnlink.value) return
  try {
    loading.value = true
    await usersStore.unlinkProvider(providerToUnlink.value)
    await authStore.checkLogin()
    toast.add({ severity: 'success', summary: `Unlinked ${providerToUnlink.value.provider}`, life: 5000 })
  } catch (e: any) {
    toast.add({ severity: 'error', summary: 'Failed to unlink provider', life: 5000 })
  } finally {
    loading.value = false
    unlinkModalVisible.value = false
    providerToUnlink.value = null
  }
}

async function deleteAccount() {
  try {
    loading.value = true
    await usersStore.deleteUser()
    await authStore.logout()
    toast.add({ severity: 'success', summary: 'Account deleted', life: 5000 })
    emit('update:visible', false)
  } catch (e: any) {
    toast.add({ severity: 'error', summary: 'Failed to delete account', life: 5000 })
  } finally {
    loading.value = false
    deleteModalVisible.value = false
  }
}

function formatDate(dateObj: any): string {
  if (!dateObj) return '—'
  const d = new Date(dateObj.dateTime || dateObj)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const primaryProvider = computed(() => {
  const user = usersStore.user
  if (!user?.authProviders || !user?.primaryProvider) return null
  const mainEmail = user.email.toLowerCase()
  const primaryProv = user.primaryProvider
  
  return user.authProviders.find(p => 
    p.email.toLowerCase() === mainEmail && p.provider === primaryProv
  )
})

const filteredAuthProviders = computed(() => {
  const user = usersStore.user
  if (!user?.authProviders || !user?.primaryProvider) return []
  const mainEmail = user.email.toLowerCase()
  const primaryProv = user.primaryProvider
  
  return user.authProviders.filter(p => 
    !(p.email.toLowerCase() === mainEmail && p.provider === primaryProv)
  )
})

function handleLogout() {
  authStore.logout()
  emit('update:visible', false)
}

async function generateQrUrl() {
  try {
    const token = executeRecaptcha ? await executeRecaptcha('signup_request') : undefined
    const res = await authStore.requestCode(usersStore.user?.email ?? '', 'qrLogin', token, undefined, usersStore.user?.userId)

    let brandName = 'login'
    if (route.path.startsWith('/odds')) {
      brandName = 'askodds'
    } else if (route.path.startsWith('/betpic')) {
      brandName = 'betpic'
    } else if (route.path.startsWith('/bet') || route.path.startsWith('/askbookmaker')) {
      brandName = 'askbookmaker'
    }

    qrUrl.value = `${getBaseUrl()}#/qrLogin/${res}?brand=${brandName}`
    logoData.value = shareService.getLogoData({ brand: brandName })
    showQrModal.value = true
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Failed to generate QR code', life: 5000 })
    console.error(err)
  }
}
function close() {
  emit('update:visible', false)
}
</script>

<template>
  <AppModal
    :visible="props.visible"
    @update:visible="emit('update:visible', $event)"
    @close="close"
    title="Settings"
    description="Manage your account"
    max-width="max-w-4xl"
    height="h-[600px]"
    has-sidebar
    main-class="relative"
    :z-index="2000"
  >
    <template #sidebar>
      <nav class="grid grid-cols-2 md:flex md:flex-col gap-1 md:max-md:pb-2">
        <button 
          @click="activeTab = 'profile'"
          :class="[
            'flex-1 flex items-center justify-center md:justify-start gap-3 px-4 py-2 md:py-3 rounded-xl transition-all font-medium text-sm whitespace-nowrap',
            activeTab === 'profile' 
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20 font-bold' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
          ]"
        >
          <User class="w-4 h-4" />
          Profile & Security
        </button>
        <button 
          @click="activeTab = 'sessions'"
          :class="[
            'flex-1 flex items-center justify-center md:justify-start gap-3 px-4 py-2 md:py-3 rounded-xl transition-all font-medium text-sm whitespace-nowrap',
            activeTab === 'sessions' 
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20 font-bold' 
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
          ]"
        >
          <ShieldCheck class="w-4 h-4" />
          Active Sessions
        </button>
      </nav>

      <div class="mt-auto pt-4 border-t border-border max-md:hidden space-y-2">
          <Button variant="ghost" class="w-full justify-start text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 gap-3 px-4 rounded-xl" @click="generateQrUrl">
            <IconQRCode class="w-4 h-4" />
            QR Login
          </Button>
          <Button variant="ghost" class="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 gap-3 px-4 rounded-xl" @click="handleLogout">
            <LogOut class="w-4 h-4" />
            Sign Out
          </Button>
      </div>
    </template>

    <div class="flex-1 overflow-y-auto overflow-x-hidden">
      <Transition name="fade-slide" mode="out-in">
        <!-- PROFILE TAB -->
        <div v-if="activeTab === 'profile'" key="profile" class="space-y-6 md:space-y-10 overflow-x-hidden">
          <!-- Personal Details Section -->
          <section>
            <div class="mb-3 md:mb-5">
               <div class="flex items-center gap-2">
                  <User class="w-5 h-5 text-gray-400" />
                  <h3 class="text-lg font-bold text-gray-900 dark:text-white">Personal Details</h3>
               </div>
               <p class="text-sm text-gray-500 mt-0.5 ml-7 max-md:hidden">Manage your account email and display name</p>
            </div>
            
            <div class="overflow-hidden rounded-xl border border-border bg-white dark:bg-zinc-950 shadow-sm">
              <!-- Email Row -->
              <div class="flex items-center gap-4 px-2 md:px-4 py-3 border-b border-gray-50 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-800/20">
                <div class="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm flex-shrink-0">
                  <IconGoogle v-if="primaryProvider?.provider === 'google'" class="w-4 h-4" />
                  <IconApple v-else-if="primaryProvider?.provider === 'apple'" class="w-4 h-4 text-gray-700 dark:text-gray-200" />
                  <Mail v-else class="w-4 h-4 text-gray-500" />
                </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight leading-none mb-1">Email Address</span>
                    <span class="text-xs font-bold text-gray-700 dark:text-gray-200 truncate">{{ usersStore.user?.email }}</span>
                  </div>
                <ShieldCheck class="w-3.5 h-3.5 ml-auto text-green-500 opacity-60" />
              </div>

              <!-- Username Row -->
              <div class="flex items-center gap-4 px-2 md:px-4 py-3 transition-colors hover:bg-gray-50/50 dark:hover:bg-white/5 relative group">
                <div class="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm flex-shrink-0">
                  <UserCircle class="w-4 h-4 text-gray-400" />
                </div>
                <div class="flex-1 flex flex-col min-w-0">
                  <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight leading-none mb-1">Username</span>
                  <Input 
                    v-model="editedUsername" 
                    class="h-5 p-0 border-none bg-transparent focus-visible:ring-0 text-xs font-bold text-gray-700 dark:text-gray-200 placeholder:text-gray-300 shadow-none"
                    placeholder="Your handle"
                  />
                </div>
                <Button 
                   v-if="isUsernameChanged"
                   @click="updateUsername"
                   variant="ghost"
                   size="sm"
                   class="text-[10px] font-bold text-primary h-7 px-3 rounded-lg hover:bg-primary/10 transition-colors"
                 >
                   Save
                 </Button>
              </div>
            </div>
          </section>

          <!-- Linked Accounts Section -->
          <section>
             <div class="flex items-center justify-between mb-3 md:mb-5">
                <div>
                   <div class="flex items-center gap-2">
                      <User class="w-5 h-5 text-gray-400" />
                      <h3 class="text-lg font-bold text-gray-900 dark:text-white">Linked Accounts</h3>
                   </div>
                   <p class="text-sm text-gray-500 mt-0.5 ml-7 max-md:hidden">Manage connected social authentication providers</p>
                </div>
                <Button variant="ghost" size="sm" class="text-xs font-bold text-primary p-0 h-auto hover:bg-transparent" @click="linkModalVisible = true">
                  + Add Account
                </Button>
             </div>
    
             <div class="overflow-hidden rounded-xl border border-border bg-white dark:bg-zinc-950 shadow-sm">
                <div 
                  v-for="provider in filteredAuthProviders" 
                  :key="provider.uniqueId"
                  class="flex items-center justify-between px-2 md:px-4 py-3 border-b last:border-0 border-gray-50 dark:border-gray-800 transition-colors hover:bg-gray-50/50 dark:hover:bg-white/5 group"
                >
                  <div class="flex items-center gap-4">
                    <div class="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-white/5 flex items-center justify-center overflow-hidden text-gray-400">
                      <IconGoogle v-if="provider.provider === 'google'" class="w-4 h-4" />
                      <IconApple v-else-if="provider.provider === 'apple'" class="w-4 h-4 text-gray-700 dark:text-gray-200" />
                      <Mail v-else class="w-4 h-4 text-gray-500" />
                    </div>
                    <div class="flex flex-col min-w-0">
                       <div class="flex items-center gap-2 mb-0.5">
                         <span class="text-[10px] font-bold text-gray-400 uppercase tracking-tight leading-none capitalize">{{ provider.provider }}</span>
                       </div>
                       <span class="text-xs font-bold text-gray-700 dark:text-gray-200 truncate">{{ provider.email }}</span>
                    </div>
                  </div>

                  <Button 
                    variant="ghost" 
                    size="sm"
                    class="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-red-500/70 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 h-8 w-8 p-0 max-md:px-0 rounded-full flex-shrink-0 transition-all duration-200"
                    @click="requestUnlink(provider)"
                  >
                    <Trash2 class="w-5 h-5" />
                  </Button>
                </div>
             </div>
          </section>
          
          <!-- Mobile-only Danger Zone -->
          <div class="mt-12 pt-8 border-t border-border md:hidden">
            <Button 
              variant="ghost" 
              class="w-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl px-6 h-12 font-bold text-sm transition-colors flex items-center justify-center gap-2" 
              @click="deleteModalVisible = true"
            >
              <Trash2 class="w-4 h-4" />
              Delete Account
            </Button>
          </div>
        </div>

        <!-- SESSIONS TAB -->
        <div v-else-if="activeTab === 'sessions'" key="sessions" class="space-y-6 overflow-x-hidden">
           <div class="flex items-center justify-between mb-6">
              <div>
                <div class="flex items-center gap-2">
                  <ShieldCheck class="w-5 h-5 text-gray-400" />
                  <h3 class="text-lg font-bold text-gray-900 dark:text-white">Active Sessions</h3>
                </div>
                <p class="text-sm text-gray-500 mt-0.5 ml-7">Devices where you're currently logged in</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                class="text-red-500 border-red-100 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/20 hover:text-red-500 rounded-xl px-6 h-11 font-bold shadow-none transition-colors ml-4" 
                @click="confirmLogoutAllVisible = true"
              >
                Sign out all
              </Button>
           </div>

           <div v-if="loading" class="py-20 flex flex-col items-center justify-center text-gray-400 italic">
              <div class="animate-spin mb-4"><ShieldCheck class="w-8 h-8 opacity-20" /></div>
              Refreshing sessions...
           </div>
           
           <div v-else class="space-y-8 pb-12">
              <SessionsTable
                :sessions="currentSession"
                :highlightCurrent="true"
                title="This Device"
                :formatDate="formatDate"
              />

              <SessionsTable
                v-if="otherSessions.length > 0"
                :sessions="otherSessions"
                :showActions="true"
                title="Other Devices"
                :onLogout="logoutSession"
                :formatDate="formatDate"
              />
           </div>
        </div>
      </Transition>
    </div>

    <div class="absolute bottom-0 left-0 right-0 p-4 border-t border-border flex max-md:flex-col items-center justify-between gap-3 bg-zinc-50/50 dark:bg-black/20 flex-shrink-0 z-10">
       <div class="flex-1 flex gap-3 order-1 md:order-2 max-md:w-full md:hidden">
         <Button variant="secondary" class="flex-1 h-11 rounded-xl font-bold gap-2" @click="generateQrUrl">
           <IconQRCode class="w-4 h-4" />
           QR Login
         </Button>
         <Button variant="ghost" class="flex-1 h-11 rounded-xl text-red-500 font-bold" @click="handleLogout">Sign Out</Button>
       </div>

       <Button variant="ghost" class="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl px-6 h-11 font-bold text-xs uppercase tracking-widest transition-colors max-md:hidden md:order-3 ml-auto" @click="deleteModalVisible = true">
         Delete Account
       </Button>
    </div>
  </AppModal>

  <!-- Confirm Delete -->
  <Dialog :open="deleteModalVisible" @update:open="deleteModalVisible = $event">
    <DialogContent class="max-w-md rounded-2xl p-6 [&>button]:hidden" :style="{ zIndex: 2100 }">
      <DialogHeader>
        <DialogTitle class="text-xl font-bold text-red-600">Delete Account Permanently?</DialogTitle>
      </DialogHeader>
      <p class="text-sm text-gray-500 mt-2 leading-relaxed">
        This action cannot be undone. All your preferences, linked accounts, and betting history associated with this account will be permanently removed.
      </p>
      <DialogFooter class="mt-6 flex gap-3">
        <Button variant="outline" class="flex-1 h-11 rounded-xl" @click="deleteModalVisible = false">Go Back</Button>
        <Button variant="destructive" class="flex-1 h-11 rounded-xl" @click="deleteAccount">Yes, Delete Everything</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Confirm Logout All -->
  <Dialog :open="confirmLogoutAllVisible" @update:open="confirmLogoutAllVisible = $event">
    <DialogContent class="max-w-md rounded-2xl p-6 [&>button]:hidden" :style="{ zIndex: 2100 }">
      <DialogHeader>
        <DialogTitle class="text-xl font-bold">Sign Out of All Devices?</DialogTitle>
      </DialogHeader>
      <p class="text-sm text-gray-500 mt-2 leading-relaxed">
        You will be logged out of every device you're currently using, including this one.
      </p>
      <DialogFooter class="mt-6 flex gap-3">
        <Button variant="outline" class="flex-1 h-11 rounded-xl" @click="confirmLogoutAllVisible = false">Cancel</Button>
        <Button variant="destructive" class="flex-1 h-11 rounded-xl" @click="logoutAllDevices">Sign Out of Everything</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Confirm Unlink -->
  <Dialog :open="unlinkModalVisible" @update:open="unlinkModalVisible = $event">
    <DialogContent class="max-w-md rounded-2xl p-6 [&>button]:hidden" :style="{ zIndex: 2100 }">
      <DialogHeader>
        <DialogTitle class="text-xl font-bold">Unlink <span class="capitalize">{{ providerToUnlink?.provider }}</span> Account?</DialogTitle>
      </DialogHeader>
      <p class="text-sm text-gray-500 mt-2 leading-relaxed">
        You are about to unlink your <strong class="capitalize">{{ providerToUnlink?.provider }}</strong> account ({{ providerToUnlink?.email }}). You will no longer be able to use this account for signing in.
      </p>
      <DialogFooter class="mt-6 flex gap-3">
        <Button variant="outline" class="flex-1 h-11 rounded-xl" @click="unlinkModalVisible = false">Cancel</Button>
        <Button variant="destructive" class="flex-1 h-11 rounded-xl" :loading="loading" @click="confirmUnlink">Unlink Account</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <LinkAccountsModal v-model:visible="linkModalVisible" :z-index="2100" />
  <QrModal 
    v-model:visible="showQrModal" 
    :url="qrUrl" 
    :logo-data="logoData || undefined"
    title="Scan to Login"
    url-label="Login URL"
    :z-index="2100"
  />
</template>

<style scoped>
@media (min-width: 768px) {
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

</style>